/**
 * Shared CSP baseline for all brands.
 *
 * Built from the “Southeastern” config as the canonical payment-first baseline,
 * then extended with any additional CSP-only domains found in other brand configs
 * (Southern / Thameslink / GreatNorthern / TransPennine / Ticketyboo).
 *
 * Brand configs should import these arrays and extend them with brand-specific
 * endpoints (e.g. brand API host, stage API host, etc).
 */

import uniq from "lodash/uniq";

const parseHostSource = (source) => {
  const match = /^(https?):\/\/(\*\.)?([^/:*]+)(:\d+)?(\/.*)?$/.exec(source);
  return match && {
    scheme: match[1],
    wildcard: !!match[2],
    host: match[3].toLowerCase(),
    port: match[4] || "",
    path: match[5] || "",
  };
};

/**
 * Whether `a` already allows everything `b` does, under CSP3 source matching. Only the cases
 * that are certain are recognised; anything else is treated as not covered and kept.
 */
const sourceCovers = (a, b) => {
  // Case-insensitive, like the host comparison below: two sources differing only in case
  // would otherwise each cover the other, and both would be dropped from the policy.
  if (a.toLowerCase() === b.toLowerCase()) {
    return false;
  }
  if (a === "https:") {
    return /^https:\/\//.test(b);
  }

  const outer = parseHostSource(a);
  const inner = parseHostSource(b);
  if (!outer || !inner || outer.scheme !== inner.scheme || outer.port !== inner.port) {
    return false;
  }

  // https://*.example.com matches any depth of subdomain (and narrower wildcards), but
  // not example.com itself.
  const hostCovered = outer.wildcard
    ? inner.host.endsWith(`.${outer.host}`)
    : !inner.wildcard && inner.host === outer.host;
  if (!hostCovered) {
    return false;
  }

  if (!outer.path) {
    return true;
  }
  return outer.path.endsWith("/") ? inner.path.startsWith(outer.path) : inner.path === outer.path;
};

/**
 * Drop sources another source in the same directive already covers - e.g. every https://
 * host in a frame-src that allows `https:`, or songbird.cardinalcommerce.com beside
 * *.cardinalcommerce.com. The policy allows exactly the same URLs, but it is now served as
 * a response header with a hard size ceiling upstream (see build/docker/bin/entrypoint), so
 * the bytes matter. The config lists keep the explicit entries for their documentation.
 */
const dropCoveredSources = (sources) =>
  uniq(sources).filter((source, _i, all) => !all.some((other) => sourceCovers(other, source)));

/**
 * Merge helper for brand configs:
 * mergeCsp(base, { cspScriptSrc: [], cspConnectSrc: [], cspFrameSrc: [], cspStyleSrc: [] })
 */
const mergeCsp = (base, overrides = {}) => ({
  cspScriptSrc: dropCoveredSources([...(base.cspScriptSrc || []), ...(overrides.cspScriptSrc || [])]),
  cspConnectSrc: dropCoveredSources([...(base.cspConnectSrc || []), ...(overrides.cspConnectSrc || [])]),
  cspFrameSrc: dropCoveredSources([...(base.cspFrameSrc || []), ...(overrides.cspFrameSrc || [])]),
  cspStyleSrc: dropCoveredSources([...(base.cspStyleSrc || []), ...(overrides.cspStyleSrc || [])]),
});

// ===== Shared CSP lists, reused across environments =====

// Base script-src (all envs)
const cspScriptSrcBase = [
  "'self'",
  "blob:",
];

// Extra script-src entries required in non-prod / sandbox / test envs
const cspScriptSrcTestExtra = [
  // Allow Usercentrics / tag manager to execute blob: scripts in non-prod envs
  "blob:",

  "https://www.sandbox.paypal.com",
  "https://otrldev.uk",
  "https://*.otrldev.uk",

  // Pay360 (hosted payment pages / widgets may inject JS)
  "https://secure.mite.pay360.com",
  "https://mite.pay360.com",
  "https://dev.mite.pay360.com",

  // Cardinal sandbox / staging JS
  "https://songbirdstag.cardinalcommerce.com",

  // Stage JP widget host (non-prod only)
  "https://widgets.stage.otrl.io",

  // Cookiebot CMP (loaded via GTM) (Southern/Thameslink/GreatNorthern)
  "https://consent.cookiebot.com",

  // Leaflet GeoSearch (map autocomplete) (Southern/Thameslink/GreatNorthern/Ticketyboo)
  "https://unpkg.com",

  // --- TransPennine / Ticketyboo extra (non-prod observed) ---
  "https://www.dwin1.com",
  "https://secure.quantserve.com",
  "https://static.sojern.com",
  "https://sc-static.net",
  "https://analytics.tiktok.com",
  "https://w.usabilla.com",
  "https://c.paypal.com",
  "https://rules.quantcount.com",
  "https://beacon.sojern.com",
  "https://lantern.roeyecdn.com",
  "https://the.sciencebehindecommerce.com",
  "https://www.awin1.com",

  // Snapchat pixel config script + further Sojern/Blis/Sovendus script hosts,
  // confirmed via CSP violation reports during GTM Custom HTML -> Custom
  // Template QA (script-src, not just connect-src)
  "https://tr.snapchat.com",
  "https://pixel.sojern.com",
  "https://tr.blismedia.com",
  "https://www.sovendus-connect.com",
];

// Extra script-src entries required in prod (and also safe to reuse in test)
const cspScriptSrcProdExtra = [
  // Google Tag Manager
  "https://www.googletagmanager.com",
  "https://assets.secure.checkout.visa.com",

  // Google reCAPTCHA (Promo Discount feature)
  "https://www.google.com",
  "https://www.gstatic.com",
  "https://*.usercentrics.eu",
  "https://app.usercentrics.eu",
  "https://web.cmp.usercentrics.eu",

  // Google Maps JS API (station autocomplete — places + geometry libraries)
  "https://maps.googleapis.com",

  // Google Pay script
  "https://pay.google.com",

  // Pay360 (hosted payment pages / widgets may inject JS in prod)
  "https://secure.pay360.com",

  // Braintree assets & JS
  "https://assets.braintreegateway.com",
  "https://js.braintreegateway.com",

  // CardinalCommerce (3DS)
  "https://songbird.cardinalcommerce.com",

  // Cardinal Trusted (3DS)
  "https://static.client.cardinaltrusted.com",

  // --- Recommended hardening: future-proof Cardinal script hosts ---
  "https://*.cardinalcommerce.com",
  "https://*.cardinaltrusted.com",

  // PayPal JS
  "https://www.paypal.com",
  "https://checkout.paypal.com",
  "https://www.paypalobjects.com",

  // PayPal utility / tracking
  "https://c.paypal.com",

  // Apple Pay JS
  "https://applepay.cdn-apple.com",

  // Sitecore Engage SDK + web personalisation scripts (AP/EU/US regions)
  // Ref: https://doc.sitecore.com/cdp/en/developers/api/integrate-engage-sdk-script.html
  "https://d1mj578wat5n4o.cloudfront.net",
  "https://d35vb5cccm4xzp.cloudfront.net",

  // Our own widgets domain
  "https://widgets.otrl.io",

  // Our infra (prod hosts)
  "https://otrl.io",
  "https://*.otrl.io",

  // Cookiebot CMP (loaded via GTM) (Southern/Thameslink/GreatNorthern)
  "https://consent.cookiebot.com",
  "https://consentcdn.cookiebot.com",

  // Leaflet GeoSearch (map autocomplete) (Southern/Thameslink/GreatNorthern/Ticketyboo)
  "https://unpkg.com",

  // --- Marketing / ad-tech scripts observed in production ---
  // Keep these here only if you truly want them as “global baseline”.
  // If not, move them to per-brand overrides.
  "https://bat.bing.com",
  "https://cdnjs.cloudflare.com",
  "https://connect.facebook.net",
  "https://snap.licdn.com",
  "https://static.ads-twitter.com",

  // TikTok / Quantcast / CreativeCDN etc (Southern/Thameslink/GreatNorthern/TPE/Ticketyboo)
  "https://analytics.tiktok.com",
  "https://secure.quantserve.com",
  "https://sc-static.net",
  "https://rules.quantcount.com",
  "https://tags.creativecdn.com",

  // Yahoo (Thameslink)
  "https://s.yimg.com",

  // Google syndication script host (Thameslink)
  "https://pagead2.googlesyndication.com",

  // AudienceManager / DMP (Southern)
  "https://akt.audiencemanager.de",

  // --- TransPennine additions (prod observed) ---
  "https://www.dwin1.com",
  "https://static.sojern.com",
  "https://w.usabilla.com",
  "https://beacon.sojern.com",
  "https://lantern.roeyecdn.com",

  // TrustArc consent manager script (TPE)
  "https://consent.trustarc.com",

  // Snapchat pixel config script + further Sojern/Blis/Sovendus script hosts,
  // confirmed via CSP violation reports during GTM Custom HTML -> Custom
  // Template QA (script-src, not just connect-src)
  "https://tr.snapchat.com",
  "https://pixel.sojern.com",
  "https://tr.blismedia.com",
  "https://www.sovendus-connect.com",
];

/**
 * Comprehensive Google country domains.
 *
 * Source: https://www.google.com/supported_domains
 *
 * Only the www. form is derived from this list. The GA4 / Google Ads conversion ping goes to
 * https://www.google.<tld>/ccm/collect, which is why the base connect-src also lists
 * https://www.google.com/ccm explicitly. Dropping the bare forms halves the list
 * (376 -> 189 entries) and takes ~3.6KB off every brand's policy, which matters now the CSP
 * is served as a response header with a hard size ceiling upstream.
 *
 * ONE bare host is still required and is declared separately in cspConnectSrcBase, next to
 * the other Google Pay entries: https://google.com, which Google Pay's pay.js posts to. It
 * was removed with the rest of the bare forms once and blocked Google Pay checkout:
 *
 *   pay.js:594 Connecting to 'https://google.com/pay' violates the following Content
 *   Security Policy directive: "connect-src ..." The action has been blocked.
 *
 * If another bare-host violation shows up, add that single domain the same way rather than
 * restoring the whole derived set.
 *
 * Note:
 * CSP does not support a pattern like google.*, and a source present in only one of two CSP
 * headers is blocked (multiple policies are enforced as an intersection), so this list can
 * only get shorter, not cleverer. Country domains must be enumerated.
 */
const googleCountryDomains = [
  "google.com",
  "google.ad",
  "google.ae",
  "google.com.af",
  "google.com.ag",
  "google.al",
  "google.am",
  "google.co.ao",
  "google.com.ar",
  "google.as",
  "google.at",
  "google.com.au",
  "google.az",
  "google.ba",
  "google.com.bd",
  "google.be",
  "google.bf",
  "google.bg",
  "google.com.bh",
  "google.bi",
  "google.bj",
  "google.com.bn",
  "google.com.bo",
  "google.com.br",
  "google.bs",
  "google.bt",
  "google.co.bw",
  "google.by",
  "google.com.bz",
  "google.ca",
  "google.cd",
  "google.cf",
  "google.cg",
  "google.ch",
  "google.ci",
  "google.co.ck",
  "google.cl",
  "google.cm",
  "google.cn",
  "google.com.co",
  "google.co.cr",
  "google.com.cu",
  "google.cv",
  "google.com.cy",
  "google.cz",
  "google.de",
  "google.dj",
  "google.dk",
  "google.dm",
  "google.com.do",
  "google.dz",
  "google.com.ec",
  "google.ee",
  "google.com.eg",
  "google.es",
  "google.com.et",
  "google.fi",
  "google.com.fj",
  "google.fm",
  "google.fr",
  "google.ga",
  "google.ge",
  "google.gg",
  "google.com.gh",
  "google.com.gi",
  "google.gl",
  "google.gm",
  "google.gr",
  "google.com.gt",
  "google.gy",
  "google.com.hk",
  "google.hn",
  "google.hr",
  "google.ht",
  "google.hu",
  "google.co.id",
  "google.ie",
  "google.co.il",
  "google.im",
  "google.co.in",
  "google.iq",
  "google.is",
  "google.it",
  "google.je",
  "google.com.jm",
  "google.jo",
  "google.co.jp",
  "google.co.ke",
  "google.com.kh",
  "google.ki",
  "google.kg",
  "google.co.kr",
  "google.com.kw",
  "google.kz",
  "google.la",
  "google.com.lb",
  "google.li",
  "google.lk",
  "google.co.ls",
  "google.lt",
  "google.lu",
  "google.lv",
  "google.com.ly",
  "google.co.ma",
  "google.md",
  "google.me",
  "google.mg",
  "google.mk",
  "google.ml",
  "google.com.mm",
  "google.mn",
  "google.com.mt",
  "google.mu",
  "google.mv",
  "google.mw",
  "google.com.mx",
  "google.com.my",
  "google.co.mz",
  "google.com.na",
  "google.com.ng",
  "google.com.ni",
  "google.ne",
  "google.nl",
  "google.no",
  "google.com.np",
  "google.nr",
  "google.nu",
  "google.co.nz",
  "google.com.om",
  "google.com.pa",
  "google.com.pe",
  "google.com.pg",
  "google.com.ph",
  "google.com.pk",
  "google.pl",
  "google.pn",
  "google.com.pr",
  "google.ps",
  "google.pt",
  "google.com.py",
  "google.com.qa",
  "google.ro",
  "google.ru",
  "google.rw",
  "google.com.sa",
  "google.com.sb",
  "google.sc",
  "google.se",
  "google.com.sg",
  "google.sh",
  "google.si",
  "google.sk",
  "google.com.sl",
  "google.sn",
  "google.so",
  "google.sm",
  "google.sr",
  "google.st",
  "google.com.sv",
  "google.td",
  "google.tg",
  "google.co.th",
  "google.com.tj",
  "google.tl",
  "google.tm",
  "google.tn",
  "google.to",
  "google.com.tr",
  "google.tt",
  "google.com.tw",
  "google.co.tz",
  "google.com.ua",
  "google.co.ug",
  "google.co.uk",
  "google.com.uy",
  "google.co.uz",
  "google.com.vc",
  "google.co.ve",
  "google.co.vi",
  "google.com.vn",
  "google.vu",
  "google.ws",
  "google.rs",
  "google.co.za",
  "google.co.zm",
  "google.co.zw",
  "google.cat",
];

const googleCountryConnectSrc = googleCountryDomains.map(
  (domain) => `https://www.${domain}`
);

// Base connect-src (all envs)
const cspConnectSrcBase = [
  "'self'",

  // Legacy native-app bridge: AppNotifyService fetches https://otrl/<event> (e.g.
  // navbar title/back updates), which the old app intercepts. Without this, CSP
  // blocks the request before the app can see it.
  "https://otrl",

  "https://*.stage.otrl.io",
  "https://*.otrldev.uk",

  // Google Tag Manager
  "https://www.googletagmanager.com",

  // Analytics
  "https://analytics.google.com",
  "https://region1.google-analytics.com",
  "https://region1.analytics.google.com",
  "https://www.google-analytics.com",
  "https://www.google.com/ccm",

  // Google Maps JS API (station autocomplete). The Maps SDK issues fetch/XHR
  // calls to maps.googleapis.com (e.g. the MapsJsInternalService/GetViewportInfo
  // RPC), which fall under connect-src. The script host is already whitelisted
  // in script-src. Sentry: BOOKING-ENGINE-PROD-3VVR
  "https://maps.googleapis.com",

  // Google country-specific domains
  ...googleCountryConnectSrc,

  // Telemetry / error logging
  "https://app.getsentry.com",

  // User tracking consent management platform
  "https://*.usercentrics.eu",
  "https://web.cmp.usercentrics.eu",
  "https://app.usercentrics.eu",
  "https://v1.api.service.cmp.usercentrics.eu",

  // Usercentrics consent + GraphQL (logs / tag manager)
  "https://consent-api.service.consent.usercentrics.eu",
  "https://graphql.usercentrics.eu",

  // Ticketyboo: additional Usercentrics backend APIs seen in logs
  "https://api.usercentrics.eu",
  "https://aggregator.service.usercentrics.eu",

  // Ad / conversion networks
  "https://ad.doubleclick.net",
  "https://adservice.google.com",
  "https://www.googleadservices.com",
  "https://stats.g.doubleclick.net",
  "https://googleads.g.doubleclick.net",
  // Floodlight /activity calls redirect to <advertiser>.fls.doubleclick.net.
  // CSP re-checks the redirect target and reports the violation against the
  // original URL, so these surfaced as ad.doubleclick.net blocks (440 events
  // over 65d, still firing on 3.4.6). Already present in cspFrameSrcBase for
  // every brand - the iframe context was fixed and the fetch context missed.
  // Sentry: BOOKING-ENGINE-PROD-3VR4 / 3V95 (CORE-23191)
  "https://fls.doubleclick.net",
  "https://*.fls.doubleclick.net",
  "https://pagead2.googlesyndication.com",
  "https://marketingplatform.google.com",

  // Meta / Facebook pixel endpoints
  "https://www.facebook.com",
  "https://connect.facebook.net",

  // Bing Ads / UET
  "https://bat.bing.com",
  "https://bat.bing.net",

  // Microsoft Clarity
  "https://*.clarity.ms",
  "https://c.bing.com",

  // LinkedIn Insight Tag
  "https://px.ads.linkedin.com",

  // Twitter / X Ads (conversion pixel)
  "https://t.co",
  "https://analytics.twitter.com",

  // Google static assets occasionally used for measurement
  "https://www.gstatic.com",

  // Google Pay
  "https://pay.google.com",
  "https://payments.google.com",
  // Google Pay's pay.js posts to https://google.com/pay — the BARE host. CSP host matching
  // is exact, so neither https://www.google.com nor https://pay.google.com covers it. This
  // entry is load-bearing for Google Pay checkout (enabled on southeastern with a live
  // merchant ID); removing it blocks the payment. Do not fold it into the country list below.
  "https://google.com",

  // PayPal production endpoints
  "https://www.paypal.com",
  "https://api.paypal.com",
  "https://b.stats.paypal.com",
  "https://c.stats.paypal.com",
  "https://c.paypal.com",
  "https://www.paypal.com/xoplatform/logger/api/logger",

  // Apple Pay gateway (merchant validation)
  "https://apple-pay-gateway.apple.com",

  // Samsung Pay
  "https://spay.samsung.com",

  // Revolut Pay payment-method manifest (Payment Request API) on /payment (CORE-24054)
  "https://merchant.revolut.com",

  // CardinalCommerce (3DS) - core script host (also used for XHR in some flows)
  "https://songbird.cardinalcommerce.com",

  // Production Braintree
  "https://payments.braintree-api.com",
  "https://api.braintreegateway.com",
  "https://client-analytics.braintreegateway.com",
  "https://assets.braintreegateway.com",

  // Pay360 production API
  "https://api.pay360.com",

  // Cookiebot
  "https://consent.cookiebot.com",
  "https://consentcdn.cookiebot.com",

  // Leaflet GeoSearch / unpkg source map fetches (Southern/Thameslink/GreatNorthern/Ticketyboo)
  "https://unpkg.com",

  // TikTok pixel API (Southern/Thameslink/GreatNorthern/TPE/Ticketyboo)
  "https://analytics.tiktok.com",

  // Quantcast pixel + error reporting (Southern/Thameslink/GreatNorthern/TPE/Ticketyboo)
  "https://pixel.quantserve.com",
  "https://pixel.quantcount.com",
  // Quantcast audience measurement — uses a randomised .icu domain (Sentry: BOOKING-ENGINE-TAB-58H)
  "https://prreqcroab.icu",

  // Snapchat pixel hosts (Southern/Thameslink/GreatNorthern/TPE/Ticketyboo)
  "https://tr.snapchat.com",
  "https://tr6.snapchat.com",

  // Sojern conversion pixel/beacon hosts, found during GTM Custom HTML -> Custom
  // Template QA (Southern/Thameslink/GreatNorthern/TPE/Ticketyboo)
  "https://pixel.sojern.com",
  "https://beacon.sojern.com",
  "https://static.sojern.com",

  // Reddit Pixel conversion beacon, fetch POST (CORE-23352). Shared by every
  // brand rather than listed per brand: the pixel is switched on and off in GTM,
  // so a per-brand list silently drifts out of step with what is actually firing
  // — TPE was missing it entirely while being the heaviest user (CORE-23382).
  "https://alb.reddit.com",

  // Bliss Media tracking + Sovendus connect, found during GTM Custom HTML ->
  // Custom Template QA (TPE)
  "https://tr.blismedia.com",
  "https://www.sovendus-connect.com",

  // Creative / ad hosting (CreativeCDN) (Southern/Thameslink/GreatNorthern)
  "https://ams.creativecdn.com",

  // Yahoo / Yimg (Thameslink)
  "https://s.yimg.com",

  // Visa SDK network calls (Thameslink)
  "https://assets.secure.checkout.visa.com",

  // Sitecore Engage
  "https://api-engage-eu.sitecorecloud.io",

  // --- TransPennine additions ---
  // TikTok IPv6 analytics endpoint (seen in stage)
  "https://analytics-ipv6.tiktokw.us",

  // Conversions API Gateway (TPE/Ticketyboo)
  "https://demo-1.conversionsapigateway.com",
  "https://mpc-prod-15-s6uit34pua-uw.a.run.app",

  // OpenStreetMap tiles and geocoding (Nominatim geosearch)
  "https://*.tile.openstreetmap.org",
  "https://nominatim.openstreetmap.org",

  // OpenAI SDK telemetry (Thameslink help widget via ticket.thameslinkrailway.com/js/common.js)
  "https://bzr.openai.com",
  "https://bzrcdn.openai.com",
];

// Extra connect-src for sandbox / PCI / test envs
const cspConnectSrcTestExtra = [
  // Braintree sandbox
  "https://payments.sandbox.braintree-api.com",
  "https://api.sandbox.braintreegateway.com",
  "https://client-analytics.sandbox.braintreegateway.com",
  "https://origin-analytics-sand.sandbox.braintree-api.com",

  // PayPal sandbox
  "https://api.sandbox.paypal.com",
  "https://www.sandbox.paypal.com",

  // --- Recommended: PayPal sandbox telemetry / logger endpoints ---
  "https://c.sandbox.paypal.com",
  "https://www.sandbox.paypal.com/xoplatform/logger/api/logger",

  // Cardinal sandbox / staging endpoints
  "https://songbirdstag.cardinalcommerce.com",
  "https://centinelapistag.cardinalcommerce.com",
  "https://geostag.cardinalcommerce.com",

  // --- Recommended fix: staging writer host (non-prod) ---
  "https://writerstag.cardinalcommerce.com",

  // Keep existing prod writer (some flows have used this historically)
  "https://writer.cardinalcommerce.com",

  // Our infra (test + pci hosts)
  "https://otrl.io",
  "https://*.otrl.io",
  "https://otrldev.uk",
  "https://*.otrldev.uk",

  // Pay360 dev (test / accreditation only)
  "https://dev.mite.pay360.com",

  // Pay360 hosted payment pages / accreditation (non-prod)
  "https://secure.mite.pay360.com",
  "https://mite.pay360.com",

  // Misc API / Cardinal telemetry (some brands list in non-prod extras)
  "https://kg668dbov0.execute-api.us-east-1.amazonaws.com",
];

// Extra connect-src for prod-only (kept separate in case we extend in future)
const cspConnectSrcProdExtra = [
  /**
   * -------------------------------
   * Cardinal / 3DS (recommended)
   * -------------------------------
   *
   * 3DS flows can touch multiple Cardinal subdomains over time.
   * Using wildcards avoids “spinner forever” failures when Cardinal introduces
   * a new host or when an edge path uses a different subdomain.
   */
  "https://*.cardinalcommerce.com",
  "https://*.cardinaltrusted.com",
  "https://static.client.cardinaltrusted.com",

  // CardinalCommerce 3-D Secure – production API (JWT init)
  "https://centinelapi.cardinalcommerce.com",

  // Cardinal auxiliary endpoints frequently used at runtime (prod equivalents of geostag / writer)
  "https://geo.cardinalcommerce.com",
  "https://writer.cardinalcommerce.com",

  // Cardinal / payment telemetry (AWS API Gateway – used by songbird)
  "https://kg668dbov0.execute-api.us-east-1.amazonaws.com",

  // Google Ads syndication endpoint (TPE)
  "https://pagead2.googlesyndication.com",

  // --- Recommended hardening: allow Pay360 secure host for XHR/fetch if used ---
  "https://secure.pay360.com",
];

// Base frame-src (used by prod and non-prod)
const cspFrameSrcBase = [
  "'self'",

  /**
   * -------------------------------
   * Any https origin may be framed
   * -------------------------------
   *
   * Required for 3DS: the issuer's ACS serves the challenge iframe from its own
   * domain, and that set cannot be enumerated (see the note further down).
   *
   * frame-src governs only what this document may embed. It is not a script
   * execution or data exfiltration channel - script-src, connect-src,
   * object-src 'none' and base-uri 'self' are unchanged, so nothing new can run
   * or read data in our origin. The residual risk is clickjacking / overlay
   * surface from a framed third party.
   *
   * Note this permits https only; http: framing stays blocked.
   */
  "https:",
  "https://www.googletagmanager.com",
  // Google reCAPTCHA challenge iframe (Promo Discount feature)
  "https://www.google.com",
  "https://*.usercentrics.eu",
  "https://app.usercentrics.eu",
  "https://web.cmp.usercentrics.eu",
  "https://widgets.otrl.io",

  // Cookiebot CMP (iframe / consent UI) (Southern/Thameslink/GreatNorthern)
  "https://consent.cookiebot.com",
  "https://consentcdn.cookiebot.com",

  // Payment frames (common)
  "https://pay.google.com",
  "https://checkout.paypal.com",
  "https://www.paypal.com",
  "https://c.paypal.com",
  "https://www.paypalobjects.com",

  "https://*.paypal.com",

  // Braintree hosted iframes (Hosted Fields / Drop-in)
  "https://assets.braintreegateway.com",

  /**
   * -------------------------------
   * Cardinal / 3DS
   * -------------------------------
   *
   * 3DS challenge presentation may use different Cardinal subdomains,
   * and (depending on issuer / flow) the framed content can move around.
   * These wildcards prevent “infinite loading” when a new Cardinal host appears.
   */
  "https://*.cardinalcommerce.com",
  "https://*.cardinaltrusted.com",
  "https://static.client.cardinaltrusted.com",

  // CardinalCommerce 3DS (live) - kept explicit for clarity
  "https://songbird.cardinalcommerce.com",

  // Cardinal merchant ACS (prod) - sometimes used for challenge presentation
  "https://0merchantacs.cardinalcommerce.com",

  /**
   * -------------------------------
   * Issuer ACS challenge iframes: covered by the `https:` scheme-source above
   * -------------------------------
   *
   * The 3DS challenge iframe is served by whichever bank issued the customer's
   * card, so the host set is unbounded and cannot be enumerated. Between
   * CORE-22953 and CORE-23383 this list grew from 43 to 71 entries across five
   * releases, and a single 12h window on one brand still produced 23 hosts we
   * had never seen. Every miss is a customer who reaches the challenge, cannot
   * complete it, and whose order is left Incomplete - and every fix needs a
   * full build and release train, because the policy ships as a build-time
   * <meta> tag in index.html (build/templates/index.html.ejs).
   *
   * The per-issuer entries that used to live here have therefore been removed
   * rather than added to. Do not reinstate them: `https:` already covers them,
   * and a partial list invites the same chase to restart.
   *
   * Removed here: verify.monzo.com, acs.revolut.com, tsys/secure4/5/7.arcot.com,
   * vs3d2verify.alinma.com, www.rsa3dsauth.co.uk, www.3ds.priorbank.by,
   * *.mpts.modirum.com, 3ds.redsys.es, 3ds-challenge.n26.com,
   * acs-challenge.apata.io, acssv.dskbank.bg, acs.eximb.com:3443,
   * *.uobgroup.com (CORE-23356), plus the 20 entries added in CORE-23383 that
   * covered 23 observed hosts (*.americanexpress.com, *.wlp-acs.com,
   * *.arcot.com, 3dsecure.starlingbank.com, acs.capitalone.com and others).
   *
   * spec/config/csp/baseCsp.spec.js asserts these stay removed, because a
   * merge from master has already reinstated them once.
   *
   * If frame-src is ever tightened back to an allowlist, the intended route is
   * to move the challenge into a self-origin shell document that carries its
   * own permissive policy - not to rebuild this list.
   */

  // Our infra (prod hosts)
  "https://otrl.io",
  "https://*.otrl.io",

  // Pay360
  "https://secure.pay360.com",

  // Addison Lee partnership widget
  "https://widget.partnerships.addisonlee.com",

  // --- TransPennine / Ticketyboo additions ---
  "https://www.sandbox.paypal.com",
  "https://c.sandbox.paypal.com",

  // Cardinal geo frame (TPE)
  "https://geo.cardinalcommerce.com",

  // Flashtalking / DoubleClick frame containers (TPE/Ticketyboo)
  "https://servedby.flashtalking.com",
  "https://*.fls.doubleclick.net",

  // Sojern iframe (TPE/Ticketyboo)
  "https://static.sojern.com",

  // Snapchat iframe (TPE/Ticketyboo)
  "https://tr.snapchat.com",

  // Facebook / Meta (pixel verification iframe)
  "https://www.facebook.com",

  // TrustArc consent UI iframe (TPE)
  "https://consent.trustarc.com",

  // Cardinal staging API sometimes used as frame in TPE/Ticketyboo list
  "https://centinelapistag.cardinalcommerce.com",

  // National Rail OJP journey planner iframe
  "https://ojp.nationalrail.co.uk",

  // Sovendus voucher iframe (TPE/Ticketyboo)
  "https://www.sovendus-connect.com",
];

// Extra frame-src for sandbox / PCI / test envs only
const cspFrameSrcTestExtra = [
  // NRE OJP journey planner staging iframe (non-prod envs use journeyPlannerNREStagingUrl)
  "https://nreojp.staging.nationalrail.co.uk",

  // PayPal sandbox
  "https://www.sandbox.paypal.com",

  // Pay360 hosted payment pages (test / accreditation)
  "https://secure.mite.pay360.com",
  "https://mite.pay360.com",
  "https://dev.mite.pay360.com",

  // Our dev domains
  "https://otrldev.uk",
  "https://*.otrldev.uk",

  /**
   * Cardinal sandbox / staging
   * (wildcards already cover these, but explicit entries keep existing intent)
   */
  "https://songbirdstag.cardinalcommerce.com",
  "https://geostag.cardinalcommerce.com",
  "https://0merchantacsstag.cardinalcommerce.com",
  "https://centinelapistag.cardinalcommerce.com",

  // TPE/Ticketyboo add these explicitly
  "https://c.sandbox.paypal.com",
];

// Base style-src (all envs)
const cspStyleSrcBase = ["'self'", "https://fonts.googleapis.com"];

// Extra style-src for test / accreditation / stage (incl. any *.otrldev.uk)
const cspStyleSrcTestExtra = [
  /**
   * -------------------------------
   * Usercentrics (required)
   * -------------------------------
   * UC UI loads CSS from these domains. Without them, UC can partially boot
   * (root exists / resources fetched) but window.UC_UI never becomes available.
   */
  "https://*.usercentrics.eu",
  "https://app.usercentrics.eu",
  "https://web.cmp.usercentrics.eu",

  "https://widgets.otrl.io",
  "https://otrl.io",
  "https://*.otrl.io",
  "https://otrldev.uk",
  "https://*.otrldev.uk",

  // Braintree drop-in CSS
  "https://assets.braintreegateway.com",

  // Google Tag Manager debug badge (GTM preview/debug mode)
  "https://www.googletagmanager.com",
];

// Extra style-src for prod (no otrldev.uk here)
const cspStyleSrcProdExtra = [
  /**
   * -------------------------------
   * Usercentrics (required)
   * -------------------------------
   */
  "https://*.usercentrics.eu",
  "https://app.usercentrics.eu",
  "https://web.cmp.usercentrics.eu",

  "https://widgets.otrl.io",
  "https://otrl.io",
  "https://*.otrl.io",

  // Braintree drop-in CSS
  "https://assets.braintreegateway.com",

  // Google Tag Manager debug badge (GTM preview/debug mode)
  "https://www.googletagmanager.com",
];

/**
 * Build base CSP for an environment.
 *
 * env: "prod" | "stage" | "test" | "accreditation"
 * - prod: base + prod extras
 * - non-prod: base + prod extras + test extras (matches current pattern)
 */
const buildBaseCspForEnv = (env) => {
  const isProd = env === "prod";
  const isNonProd = env === "stage" || env === "test" || env === "accreditation";

  return {
    cspScriptSrc: uniq([
      ...cspScriptSrcBase,
      ...cspScriptSrcProdExtra,
      ...(isNonProd ? cspScriptSrcTestExtra : []),
    ]),
    cspConnectSrc: uniq([
      ...cspConnectSrcBase,
      ...cspConnectSrcProdExtra,
      ...(isNonProd ? cspConnectSrcTestExtra : []),
    ]),
    cspFrameSrc: uniq([
      ...cspFrameSrcBase,
      ...(isNonProd ? cspFrameSrcTestExtra : []),
    ]),
    cspStyleSrc: uniq([
      ...cspStyleSrcBase,
      ...(isProd ? cspStyleSrcProdExtra : cspStyleSrcTestExtra),
    ]),
  };
};

export default {
  uniq,
  mergeCsp,
  sourceCovers,
  dropCoveredSources,
  buildBaseCspForEnv,

  // exported lists (so brand configs can compose exactly like today if preferred)
  googleCountryDomains,
  googleCountryConnectSrc,

  cspScriptSrcBase,
  cspScriptSrcProdExtra,
  cspScriptSrcTestExtra,

  cspConnectSrcBase,
  cspConnectSrcProdExtra,
  cspConnectSrcTestExtra,

  cspFrameSrcBase,
  cspFrameSrcTestExtra,

  cspStyleSrcBase,
  cspStyleSrcProdExtra,
  cspStyleSrcTestExtra,
};
